import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  type Header,
  type HeaderGroup,
  flexRender,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import React, { type CSSProperties, useMemo } from "react";
import { useIntl } from "react-intl";

import { CustomToolTip } from "../ui/CustomToolTip";

import { getCommonPinningStyles } from "./ColumnPinning";
import { wpxTableMessages } from "./wpxTable.intl";

import {
  ACTION_ID,
  ROW_DND_DRAG_HANDLE_ID,
  SELECTOR_ID,
} from "@/app/constants/global-constants";
import { getColumnWidth } from "@/components/dataTable/utils/tableUtils";
import type {
  ResponsePagination,
  SortDirection,
} from "@/components/pagination/hooks/usePagination";
import { useTheme } from "@/components/ThemeProvider";
import { Button } from "@/components/ui/button";
import * as Table from "@/components/ui/table";
import { cn } from "@/utils/core/generalUtils";

interface TableHeaderProps<T> {
  headerGroup: HeaderGroup<T>;
  pagination?: ResponsePagination;
  columnOrder: string[];
  dataLength: number;
  isMobile?: boolean;
  enableColumnResizing?: boolean;
  columnDraggable?: boolean;
  isLoading?: boolean;
  rowSelection?: Record<string, boolean>;
}

type ExtendedHeader<T> = Header<T, unknown> & {
  column: {
    columnDef: {
      meta?: {
        isColumnOrderable?: boolean;
      };
    };
  };
};

interface DraggableTableHeaderProps<T> {
  header: ExtendedHeader<T>;
  pagination: ResponsePagination | undefined;
  isActionIdLastColumn: boolean;
  isMobile?: boolean;
  enableColumnResizing?: boolean;
  isTableDragDisabled?: boolean;
  isLoading?: boolean;
}

const SortIconComponent = (props: {
  direction?: SortDirection;
}): JSX.Element => {
  const { direction } = props;

  if (!direction) {
    return <ChevronsUpDown size={19} />;
  }

  if (direction === "asc") {
    return <ArrowUp size={19} />;
  }

  return <ArrowDown size={19} />;
};

const SortIcon = React.memo(SortIconComponent);

const SortLabelComponent = (props: {
  direction?: SortDirection;
}): JSX.Element => {
  const { direction } = props;
  const intl = useIntl();

  if (!direction) {
    return <span>{intl.formatMessage(wpxTableMessages.sortAscending)}</span>;
  }

  if (direction === "asc") {
    return <span>{intl.formatMessage(wpxTableMessages.sortedAscending)}</span>;
  }

  return <span>{intl.formatMessage(wpxTableMessages.sortedDescending)}</span>;
};

const SortLabel = React.memo(SortLabelComponent);

const DraggableTableHeader = <T,>(
  props: DraggableTableHeaderProps<T>,
): JSX.Element | null => {
  const {
    header,
    pagination,
    isActionIdLastColumn,
    isMobile,
    enableColumnResizing,
    isTableDragDisabled = false,
    isLoading = false,
  } = props;

  const { theme: skin } = useTheme();

  const handleSort = pagination?.handleSort;
  const getSortDirection = pagination?.getSortDirection;
  const isSorted = pagination?.isSorted;

  const { column } = header;
  const canSort = column.getCanSort();

  const sortDirection = column.getIsSorted();

  const isSelectId = header.column.columnDef.id === SELECTOR_ID;
  const isActionId = header.column.columnDef.id === ACTION_ID;
  const isRowDragHandleId =
    header.column.columnDef.id === ROW_DND_DRAG_HANDLE_ID;
  const isPinned = header.column.getIsPinned();

  const isColumnOderable =
    header.column.columnDef.meta?.isColumnOrderable ?? true;

  const isDragDisabled =
    isSelectId ||
    isActionId ||
    Boolean(isPinned) ||
    !isColumnOderable ||
    isTableDragDisabled;

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    isOver,
    transform,
    transition,
  } = useSortable({
    id: header.column.id,
    disabled: isDragDisabled || isLoading,
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    position: isDragging ? "relative" : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  const pinningStyles = useMemo(
    () =>
      getCommonPinningStyles({
        column: header.column,
        isRow: false,
        disablePinning: isMobile,
      }),
    [header.column, isMobile],
  );

  const columnRelativeDepth = header.depth - header.column.depth;

  if (columnRelativeDepth > 1) {
    return null;
  }

  let rowSpan = 1;
  if (header.isPlaceholder) {
    const leafs = header.getLeafHeaders();
    rowSpan = leafs[leafs.length - 1].depth - header.depth;
  }
  const hasParentHeader = header.depth > 1;

  // @ts-expect-error sortBy exists
  const headerSortBy = header.column.columnDef.sortBy;

  // Use pagination sorting if available, otherwise use TanStack Table's built-in sorting
  let isColumnSorted = false;
  if (pagination) {
    isColumnSorted = isSorted ? isSorted(headerSortBy) : false;
  } else {
    isColumnSorted = canSort && sortDirection !== false;
  }

  // Get sort direction for display
  const getSortDirectionForDisplay = (): SortDirection | undefined => {
    if (pagination && getSortDirection) {
      return getSortDirection(headerSortBy);
    }
    if (sortDirection === "asc") {
      return "asc";
    }
    if (sortDirection === "desc") {
      return "desc";
    }

    return undefined;
  };

  const onResizeMouseDown = (e: React.MouseEvent): void => {
    const resizeHandler = header.getResizeHandler();
    resizeHandler(e);
  };

  const onResizeTouchStart = (e: React.TouchEvent): void => {
    const resizeHandler = header.getResizeHandler();
    resizeHandler(e);
  };

  return (
    <>
      <Table.TableHead
        colSpan={header.colSpan}
        style={{
          minWidth: header.column.columnDef.meta?.minWidth,
          height: hasParentHeader ? "0px" : "auto",
          paddingBottom: hasParentHeader ? "10px" : "auto",
          ...style,
          ...((enableColumnResizing || isLoading) && {
            width: getColumnWidth(header, isLoading),
          }),
          ...pinningStyles,
        }}
        className={cn(
          "text-primary capitalize relative group border-r border-white last:border-r-0",
          skin === "dark" && "text-white",
          skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight",
          isOver && !isDragDisabled && "border-r-primary",
          (isSelectId || isRowDragHandleId) && "border-r-0",
          isActionIdLastColumn && "[&:nth-last-child(2)]:border-r-0",
          header.column?.columnDef?.meta?.className,
          !isDragDisabled && !isDragging && "cursor-grab",
          isDragDisabled && "cursor-default",
          isDragging && !isDragDisabled && "cursor-grabbing",
          isSelectId && "!max-w-[32px]",
          isRowDragHandleId && "px-0",
        )}
        ref={setNodeRef}
        rowSpan={rowSpan}
        {...(!isDragDisabled && attributes)}
        {...(!isDragDisabled && listeners)}
      >
        <div
          className={cn(
            "flex items-center !min-w-fit whitespace-nowrap text-sm font-medium ",
          )}
        >
          <div
            className='flex items-center h-10 gap-2 px-4 py-2 pl-0 capitalize border-0 ring-0 hover:bg-inherit hover:text-inherit w-full justify-between'
            style={{
              height: hasParentHeader ? "0px" : "auto",
              ...header.column.columnDef.meta?.headerStyle,
            }}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
            {(headerSortBy || canSort) && (
              <CustomToolTip
                toolTipContent={
                  <span>
                    <SortLabel direction={getSortDirectionForDisplay()} />
                  </span>
                }
                toolTipTrigger={
                  <Button
                    variant='ghost'
                    className={cn(
                      "hover:bg-slate-100 px-0 h-auto p-0.5 rounded-sm  opacity-0 group-hover:opacity-100 cursor-pointer",
                      isColumnSorted && "opacity-100",
                    )}
                    onClick={() => {
                      if (pagination && handleSort) {
                        // Use pagination sorting if available
                        handleSort(headerSortBy);
                      } else if (canSort) {
                        // Use TanStack Table's built-in sorting
                        column.toggleSorting();
                      }
                    }}
                  >
                    <div
                      className='w-full flex items-center justify-center'
                      data-no-dnd='true'
                    >
                      <SortIcon direction={getSortDirectionForDisplay()} />
                    </div>
                  </Button>
                }
                toolTipContentProps={{
                  align: "center",
                  side: "bottom",
                }}
              />
            )}
          </div>
        </div>
        {header.column.getCanResize() && !isSelectId && !isActionId && (
          <div
            data-no-dnd='true'
            onMouseDown={onResizeMouseDown}
            onTouchStart={onResizeTouchStart}
            className={cn(
              "absolute top-0 right-0 h-full w-1 cursor-col-resize select-none touch-none rounded-full column-resizer",
              "bg-gray-300 opacity-0 group-hover:opacity-100",
              header.column.getIsResizing() && "bg-primary opacity-100",
            )}
          />
        )}
      </Table.TableHead>
    </>
  );
};

/**
 * Renders a table header row with sortable, draggable columns.
 *
 * @param {TableHeaderProps<T>} props - Component props.
 * @returns {React.JSX.Element} Rendered table header row.
 */
const TableHeaderComponent = <T,>(
  props: TableHeaderProps<T>,
): React.JSX.Element => {
  const {
    headerGroup,
    pagination,
    columnOrder,
    dataLength,
    isMobile,
    enableColumnResizing,
    columnDraggable = true,
    isLoading = false,
  } = props;

  const draggableColumnOrder = React.useMemo(() => {
    const pinnedColumnIds = new Set(
      headerGroup.headers
        .filter((header) => header.column.getIsPinned())
        .map((header) => header.column.id),
    );
    return columnOrder.filter((id) => !pinnedColumnIds.has(id));
  }, [columnOrder, headerGroup.headers]);

  const lastVisibleHeaderId =
    headerGroup.headers[headerGroup.headers.length - 1]?.column.id;
  const isActionIdLastColumn = lastVisibleHeaderId === ACTION_ID;

  return (
    <Table.TableRow key={headerGroup.id}>
      <SortableContext
        items={draggableColumnOrder}
        strategy={horizontalListSortingStrategy}
        disabled={dataLength === 0 || !columnDraggable || isLoading}
      >
        {headerGroup.headers.map((header) => {
          return (
            <DraggableTableHeader
              key={`${header.id}`}
              header={header}
              pagination={pagination}
              isMobile={isMobile}
              enableColumnResizing={enableColumnResizing}
              isActionIdLastColumn={isActionIdLastColumn}
              isTableDragDisabled={
                dataLength === 0 || !columnDraggable || isLoading
              }
              isLoading={isLoading}
            />
          );
        })}
      </SortableContext>
    </Table.TableRow>
  );
};

const TableHeader = React.memo(
  TableHeaderComponent,
) as typeof TableHeaderComponent;

export default TableHeader;
